<template>
	<div style="min-width: 500px; min-height: 100px;">
		<div v-if="!isLoading">
			<div>
				<BaseDatepicker
					ref="BaseInput-expertise"
					:required="true"
					:label="$t('examined_at')"
					type="text"
					error-behavior="blur"
					v-model="examinationDate"
					class="w-1/2"
					:disabled="false"
					:max_date="new Date()"
					:min_date="getMinDate()"
				/>
			</div>

			<div class="flex justify-end">
				<SaveButton @click="save" class="mb-4 btn-blue" :disabled="!examinationDate || !hasChanges || isSaving">
					{{ $t('general.save') }}
				</SaveButton>
			</div>
		</div>

		<LoadingSpinner v-else />
	</div>
</template>

<script>
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import Authentication from '@/mixins/Authentication';
import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';
import BaseDatepicker from '@/components/forms/inputs/BaseDatepicker.vue';
import SaveButton from '@/components/forms/buttons/SaveButton.vue';

export default {
	name: 'DamageAssessorCaseSetExaminationModal',

	components: {
		SaveButton,
		BaseDatepicker,
		LoadingSpinner,
	},

	mixins: [Authentication],

	data() {
		return {
			damageAssessorCase: null,

			isLoading: true,
			isSaving: false,

			examinationDate: null,

			/*
			 * Original persisted value.
			 * Used only for dirty-state comparison.
			 */
			originalExaminationDate: null,
		};
	},

	computed: {
		hasChanges() {
			return this.normalizeDate(this.examinationDate) !== this.normalizeDate(this.originalExaminationDate);
		},
	},

	watch: {
		hasChanges(value) {
			this.$store.commit('setConfirmLeaving', value ? this.$t('leave_page') : false);
		},
	},

	mounted() {
		this.getDamageAssessorCaseData();
	},

	destroyed() {
		this.$store.commit('setConfirmLeaving', false);
	},

	methods: {
		getMinDate() {
			if (this.damageAssessorCase?.appointment_agreed_at) {
				return new Date(this.damageAssessorCase.appointment_agreed_at);
			}

			return null;
		},

		getDamageAssessorCaseData() {
			this.isLoading = true;

			new DamageAssessorCaseRepository()
				.getDamageAssessorCaseDetail(this.$route.params.id)
				.then(response => {
					this.damageAssessorCase = response.data;

					if (this.damageAssessorCase?.examined_at) {
						this.examinationDate = new Date(this.damageAssessorCase.examined_at);
					} else {
						this.examinationDate = null;
					}

					this.originalExaminationDate = this.examinationDate ? new Date(this.examinationDate) : null;
				})
				.catch(error => {
					console.log(error);
				})
				.finally(() => {
					this.isLoading = false;
				});
		},

		normalizeDate(value) {
			if (!value) {
				return null;
			}

			const date = value instanceof Date ? value : new Date(value);

			if (Number.isNaN(date.getTime())) {
				return null;
			}

			return date.getTime();
		},

		save() {
			if (this.isSaving || !this.examinationDate || !this.hasChanges) {
				return;
			}

			this.isSaving = true;

			new DamageAssessorCaseRepository()
				.setExaminationDate(this.$route.params.id, this.examinationDate)
				.then(() => {
					this.$root.$emit('updateCase');

					this.$store.commit('setConfirmLeaving', false);

					return this.$swal(
						this.$t('general.success').toString(),

						this.$t('general.changes_saved').toString(),

						'success',
					);
				})
				.then(() => {
					this.$router.go(-1);
				})
				.catch(error => {
					console.log(error);

					/*
					 * Save failed, therefore the currently
					 * selected value is still unsaved.
					 *
					 * We intentionally keep the dirty-state
					 * warning active.
					 */
					this.$swal(
						this.$t('general.error').toString(),

						this.$t('general.changes_not_saved').toString(),

						'error',
					);
				})
				.finally(() => {
					this.isSaving = false;
				});
		},
	},
};
</script>
